/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { t } from "i18next";
import React from "react";

interface OrganismTest {
  id: string;
  name: string;
  observed: boolean;
  cfu?: string;
  remarks: string;
}

interface MicrobialGrowthRecordingProps {
  organisms: OrganismTest[];
  addOrganism: () => void;
  removeOrganism: (id: string) => void;
  updateOrganism: (
    id: string,
    field: keyof OrganismTest,
    value: any
  ) => void;
  organismTypes: any[];
  errors: {
    organisms?: string;
    [key: string]: string | undefined;
  };
  setErrors: React.Dispatch<React.SetStateAction<any>>;
}

const MicrobialGrowthRecording: React.FC<
  MicrobialGrowthRecordingProps
> = ({
  organisms,
  addOrganism,
  removeOrganism,
  updateOrganism,
  organismTypes,
  errors,
  setErrors
}) => {
  return (
    <>
    <section className="bg-white rounded-[1rem] border border-slate-200 overflow-hidden animate-in fade-in duration-500 delay-150">
            <div className="px-6 py-1.5 bg-slate-50/30 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-xl bg-[#2D3E50] text-white flex items-center justify-center text-sm font-black shadow-sm">03</div>
                <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-[0.2em]">{t("gptTest.gptTestDetails.microGrowth")}</h3>
              </div>
              <button 
                onClick={addOrganism}
                className="flex items-center gap-2 px-3 py-1.5 border border-orange-500 text-orange-500 rounded-lg text-[10px] font-black uppercase tracking-[0.12em] bg-white hover:bg-orange-60 active:scale-95 transition-all"
              >
                <span className="material-symbols-outlined text-xl font-black">{t("gptTest.icons.add_circle")}</span>
                {t("gptTest.gptTestDetails.addRow")}
              </button>
            </div>
            <div className="p-0 overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-[#2D3E50] text-white text-[11px] font-black uppercase tracking-widest">
                  <tr>
                    <th className="px-6 py-3 whitespace-nowrap">{t("gptTest.gptTestDetails.MicroIdentifier")} <span className="text-red-500">*</span></th>
                    <th className="px-6 py-3 text-center whitespace-nowrap">{t("gptTest.gptTestDetails.growthObserved")}</th>
                    <th className="px-6 py-3 text-center whitespace-nowrap">{t("gptTest.gptTestDetails.cfuCount")}</th>
                    <th className="px-6 py-3 whitespace-nowrap">{t("gptTest.gptTestDetails.laboratoryRemarks")}</th>
                    <th className="px-6 py-3 text-right whitespace-nowrap"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {organisms.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-12 py-2 text-center">
                         <div className="flex flex-col items-center gap-3 text-slate-300">
                            <span className="material-symbols-outlined text-2xl">{t("gptTest.icons.biotech")}</span>
                            <p className="text-[12px] font-bold tracking-widest italic">{t("gptTest.gptTestDetails.addRowDescription")}</p>
                         </div>
                      </td>
                    </tr>
                  ) : (
                    organisms.map((o) => (
                      <tr key={o.id} className={`transition-all duration-500 ${o.observed ? 'bg-amber-50/50' : 'bg-white hover:bg-slate-50/50'}`}>
                        <td className="px-7 py-3">
                           <div className="relative w-[200px]">
                             <span className="material-symbols-outlined absolute left-0 top-1/2 -translate-y-1/2 text-slate-300 text-xl opacity-0 group-focus-within:opacity-100 transition-opacity">{t("gptTest.icons.microbiology")}</span>
                             <select
                                value={o.name}
                               onChange={(e) => {
                                  updateOrganism(o.id, "name", e.target.value);

                                  setErrors((prev: any) => ({
                                    ...prev,
                                    [`organism_${organisms.indexOf(o)}`]: "",
                                  }));
                                }}
                                className="w-full h-10 px-3 pr-4 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:border-[#FF7043] focus:ring-4 focus:ring-orange-500/5 transition-all appearance-none"
                              >
                                <option value="">{t("gptTest.gptTestDetails.selectMicroorganism")}</option>

                                {organismTypes.map((item: any) => (
                                  <option key={item.id} value={item.organism_type}>
                                    {item.organism_type}
                                  </option>
                                ))}
                              </select>
                              <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">
                                {t("gptTest.icons.expand_more")}
                              </span>
                              {errors[`organism_${organisms.indexOf(o)}`] && <p className="text-[11px] text-red-500 mt-1">{errors[`organism_${organisms.indexOf(o)}`]}</p>}
                           </div>
                        </td>
                        <td className="px-8 py-3">
                           <div className="flex items-center justify-center gap-6 scale-110">
                              <span className={`text-[11px] uppercase tracking-tighter transition-colors ${!o.observed ? 'text-slate-800' : 'text-slate-200'}`}>{t("gptTest.gptTestDetails.negative")}</span>
                              <button 
                                onClick={() => updateOrganism(o.id, 'observed', !o.observed)}
                                className={`w-16 h-8 rounded-full relative transition-all shadow-inner border-0 outline-none ring-0 focus:outline-none focus:ring-0 focus:border-0 appearance-none ${o.observed ? 'bg-emerald-500' : 'bg-slate-200'}`}
                              >
                              <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-lg transition-all duration-300 ${o.observed ? "left-[38px]" : "left-[2px]"}`}></div>
                              </button>
                              <span className={`text-[11px] uppercase tracking-tighter transition-colors ${o.observed ? 'text-emerald-600' : 'text-slate-200'}`}>{t("gptTest.gptTestDetails.positive")}</span>
                           </div>
                        </td>
                        <td className="px-8 py-3">
                           <div className="flex justify-center">
                             <input 
                              type="number" 
                              placeholder="--" 
                              disabled={!o.observed}
                              value={o.cfu}
                              onChange={(e) => updateOrganism(o.id, 'cfu', e.target.value)}
                              className={`w-24 text-center rounded-lg py-2 text-sm outline-none border-0 focus:border-0 focus:outline-none focus:ring-0 transition-all duration-300 ${
                                o.observed
                                  ? 'bg-white text-emerald-700 shadow-md'
                                  : 'bg-slate-100 text-slate-300 cursor-not-allowed opacity-30'
                              }`} 
                             />
                           </div>
                        </td>
                        <td className="px-8 py-3">
                           <input 
                            type="text" 
                            placeholder="Observation notes..." 
                            value={o.remarks}
                            onChange={(e) => updateOrganism(o.id, 'remarks', e.target.value)}
                            className="relative w-[200px] h-10 px-3 pr-4 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:border-[#FF7043] focus:ring-4 focus:ring-orange-500/5 transition-all appearance-none" 
                           />
                        </td>
                        <td className="px-8 py-3 text-right">
                           <button 
                            onClick={() => removeOrganism(o.id)}
                            className="w-12 h-12 flex items-center justify-center rounded-2xl border-0 outline-none ring-0 appearance-none focus:border-0 focus:outline-none focus:ring-0 hover:bg-red-50 hover:text-red-500 text-slate-300 transition-all active:scale-90"
                           >
                             <span className="material-symbols-outlined text-2xl">{t("gptTest.icons.delete_sweep")}</span>
                           </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-12 py-6 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3 text-slate-400">
                 {/* <span className="material-symbols-outlined text-lg">history_edu</span> */}
                 {/* <p className="text-[11px] font-bold uppercase tracking-widest italic">
                   Recorded By: <span className="text-slate-600 font-black not-italic ml-1">P Shyam Kumar [ADM-102]</span> 
                   <span className="mx-6 text-slate-200">|</span> 
                   Reading Date & Time: <span className="text-slate-600 font-black not-italic ml-1 font-mono uppercase">{new Date().toLocaleDateString()} 15:42</span>
                 </p> */}
              </div>
            </div>
          </section>
    </>
    );
};

export default MicrobialGrowthRecording;